import React, { useEffect, useState } from "react";
import { Select, Time } from "../design/Shared/Select";
import { Sparkles } from "lucide-react";
import TestScheduledQuestion from "./TestScheduledQuestion";
import ScheduleFilesWarning from "../design/Query/ScheduleFilesWarning";
import EmailDeliveryOptions, {
  describeRecipients,
  emailSettingsFor,
  ownerEmailOf,
} from "../design/Query/EmailDeliveryOptions";
import { FEATURE_FLAGS, useFeatureFlag } from "../context/FeatureFlagsContext";

// step: 'schedule' | 'confirm' | 'test'

const NotificationScheduler = ({ selectedQuestion, onClose, onSchedule, handleSelectedPrompt, userData, uploadedFiles }) => {
  const [frequency, setFrequency] = useState("daily");
  const [selectedAnswer, setSelectedAnswer] = useState(0);
  const [proposedQuestions, setProposedQuestions] = useState(null);
  const [scheduleDetails, setScheduleDetails] = useState({
    daily: { hour: "09" },
    weekly: { day: "Monday" },
    monthly: { day: "5" },
  });
  const [step, setStep] = useState("schedule");

  // Email delivery: on by default for the owner's own address — receiving the answer is the
  // point of scheduling it — and off only when the GreenRoad profile has no address.
  // The whole option is behind the scheduled_email feature flag (back office): while it is off
  // the popup never mentions email and the schedule goes out without an email part.
  const emailFeatureOn = useFeatureFlag(FEATURE_FLAGS.SCHEDULED_EMAIL);
  const ownerEmail = emailFeatureOn ? ownerEmailOf(userData) : null;
  const [sendToMe, setSendToMe] = useState(!!ownerEmail);
  const [extraRecipients, setExtraRecipients] = useState([]);

  const handleFrequencyChange = (e) => {
    setFrequency(e.target.value);
  };

  const updateScheduleDetail = (type, value) => {
    setScheduleDetails((prev) => ({
      ...prev,
      [frequency]: { ...prev[frequency], [type]: value },
    }));
  };

  const buildScheduleConfig = (question) => ({
    question,
    frequency,
    details: scheduleDetails[frequency],
    email: emailFeatureOn ? emailSettingsFor(sendToMe, ownerEmail, extraRecipients) : null,
  });

  const handleScheduleClick = () => {
    setStep("confirm");
  };

  const handleScheduleNow = () => {
    onSchedule(buildScheduleConfig(proposedQuestions[selectedAnswer]));
  };

  const handleScheduleNowFromTest = (editedQuestion) => {
    onSchedule(buildScheduleConfig(editedQuestion));
  };

  const handleTestItBefore = () => {
    setStep("test");
  };

  const handleBackToEdit = () => {
    setStep("schedule");
  };

  const renderFrequencyOptions = () => {
    switch (frequency) {
      case "daily":
        return (
          <div className="flex flex-col gap-1.5 max-w-[200px] w-48">
            <Time
              className="w-48 max-w-[260px]"
              header={"At The Following Time"}
              value={scheduleDetails.daily.hour}
              onChange={(value) => updateScheduleDetail("hour", value)}
            />
          </div>
        );
      case "weekly":
        return (
          <Select
            className="w-48 max-w-[260px]"
            onChange={(e) => updateScheduleDetail("day", e.target.value)}
            title={"On The Following Day"}
            options={[
              { title: "Monday", value: "Monday" },
              { title: "Tuesday", value: "Tuesday" },
              { title: "Wednesday", value: "Wednesday" },
              { title: "Thursday", value: "Thursday" },
              { title: "Friday", value: "Friday" },
              { title: "Saturday", value: "Saturday" },
              { title: "Sunday", value: "Sunday" },
            ]}
            value={scheduleDetails.weekly.day}
          />
        );
      case "monthly":
        return (
          <Select
            className="w-48 max-w-[300px]"
            title={"Select Monthly Day"}
            value={scheduleDetails.monthly.day}
            onChange={(e) => updateScheduleDetail("day", e.target.value)}
            options={[
              { title: "1", value: "1" },
              { title: "5", value: "5" },
              { title: "10", value: "10" },
              { title: "15", value: "15" },
              { title: "20", value: "20" },
              { title: "25", value: "25" },
              { title: "28", value: "28" },
            ]}
            wrappers={["Day", "of every month"]}
          />
        );
      default:
        return null;
    }
  };

  const handleQuestionChange = (index, newText) => {
    setProposedQuestions((prev) => {
      const newArray = [...prev];
      newArray[index] = newText;
      return newArray;
    });
  };

  useEffect(() => {
    if (proposedQuestions === null) {
      const processSchedulableQuestions = async () => {
        setProposedQuestions([selectedQuestion]);
      };
      processSchedulableQuestions();
    }
  }, []);

  if (step === "test") {
    return (
      <TestScheduledQuestion
        question={proposedQuestions?.[selectedAnswer] ?? selectedQuestion}
        onBack={handleBackToEdit}
        onScheduleNow={handleScheduleNowFromTest}
        userData={userData}
      />
    );
  }

  if (step === "confirm") {
    const currentQuestion = proposedQuestions?.[selectedAnswer] ?? selectedQuestion;

    return (
      <div className="w-full md:min-w-[700px]">
        <div className="text-xl text-dark-blue-100 font-bold py-4 px-6 border-b border-whitesmoke-100 flex items-center justify-between">
          Test the Prompt Before Scheduling It
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="hover:cursor-pointer"
            onClick={onClose}
          >
            <path d="M4 4L16 16M16 4L4 16" stroke="#303C54" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="flex flex-col p-6 gap-4">
          <p className="font-bold text-dark-blue-100 text-sm">Current Prompt:</p>
          <textarea
            rows={4}
            value={currentQuestion}
            readOnly
            style={{ resize: "none" }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-dark-blue-100 focus:outline-none bg-white cursor-default text-sm leading-relaxed"
          />
          {emailFeatureOn && (
            <p className="text-[13px] text-dimgray-200" data-testid="email-summary">
              {describeRecipients(sendToMe, ownerEmail, extraRecipients)
                ? `Each answer will also be emailed to ${describeRecipients(sendToMe, ownerEmail, extraRecipients)}, with a PDF copy attached.`
                : "The answers will only appear here, under Scheduled Tasks — nobody will be emailed."}
            </p>
          )}
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => setStep("schedule")}
              className="border rounded-2xl py-2.5 px-6 border-dimgray-200 text-dark-blue-100 font-semibold text-sm"
            >
              Back
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleScheduleNow}
                className="border rounded-2xl py-2.5 px-6 border-dimgray-200 text-dark-blue-100 font-semibold text-sm"
              >
                Schedule Prompt
              </button>
              <button
                onClick={handleTestItBefore}
                className="border rounded-2xl py-2.5 px-6 border-dimgray-200 bg-dark-blue-100 text-white font-bold text-sm"
              >
                Test Prompt
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full md:min-w-[770px]" style={{ minHeight: "500px" }}>
        <div className="text-xl text-dark-blue-100 font-bold py-4 px-6 border-b border-whitesmoke-100 flex items-center justify-between">
          Schedule a Task with Mila
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="hover:cursor-pointer"
            onClick={onClose}
          >
            <path
              d="M4 4L16 16M16 4L4 16"
              stroke="#303C54"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        {proposedQuestions !== null && proposedQuestions.length > 0 && (
          <div className="space-y-7 flex flex-col p-6" style={{ minHeight: "440px" }}>
            <ScheduleFilesWarning files={uploadedFiles} />
            <div className="flex flex-col gap-1.5">
              <p className="font-semibold text-dark-blue-100 pb-5">
                Select and Edit the Question You'd Like to Schedule:
              </p>
              {proposedQuestions &&
                proposedQuestions.map((value, index) => (
                  <p className="text-violet-100 pb-5 w-full" key={`question-${index}`}>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="question"
                        value={index}
                        checked={selectedAnswer == index}
                        onChange={(e) => {
                          setSelectedAnswer(e.target.value);
                          handleSelectedPrompt(value);
                        }}
                        style={{
                          width: "15px",
                          height: "15px",
                          "accent-color": "#303C54",
                          display: "None",
                        }}
                      />
                      {selectedAnswer == index ? (
                        <textarea
                          rows={Math.ceil(value.length / 80)}
                          value={value}
                          onChange={(e) => handleQuestionChange(index, e.target.value)}
                          className="bg-transparent p-2 border border-violet-200 rounded text-violet-100 focus:outline-none focus:border-violet-300 w-full custom-scheduleoption-scroll"
                          style={{ borderRadius: "10px", resize: "none", width: "100%" }}
                          autoFocus
                        />
                      ) : (
                        <span style={{ padding: "10px 10px" }}>{value}</span>
                      )}
                    </label>
                  </p>
                ))}
            </div>
            <div className="flex gap-6">
              <Select
                className="w-48"
                title={"Will Repeat Every"}
                value={frequency}
                onChange={handleFrequencyChange}
                options={[
                  { title: "Day", value: "daily" },
                  { title: "Week", value: "weekly" },
                  { title: "Month", value: "monthly" },
                ]}
                toLowerCase
              />
              {renderFrequencyOptions()}
            </div>
            {emailFeatureOn && (
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-dark-blue-100">Email delivery</p>
                <EmailDeliveryOptions
                  ownerEmail={ownerEmail}
                  sendToMe={sendToMe}
                  onSendToMeChange={setSendToMe}
                  extraRecipients={extraRecipients}
                  onExtraRecipientsChange={setExtraRecipients}
                  compact
                />
              </div>
            )}
            <div className="flex gap-3 justify-end" style={{ marginTop: "16px" }}>
              <button
                onClick={onClose}
                className="border rounded-2xl py-3 w-[92px] border-dimgray-200 text-dark-blue-100 font-semibold leading-4.5"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleClick}
                className="border rounded-2xl py-3 w-[92px] border-dimgray-200 bg-dark-blue-100 text-white font-bold leading-4.5"
              >
                Schedule
              </button>
            </div>
          </div>
        )}
        {proposedQuestions === null && (
          <div className="flex items-center justify-center h-64">
            <Sparkles className="text-violet-500 animate-spin-infinite2" size={52} />
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationScheduler;
